import { Ban, CheckCircle2, Loader2, XCircle } from 'lucide-react';

export type AttendanceMarkStatus = 'attended' | 'missed' | 'cancelled';

export interface AttendanceToggleProps {
  current?: 'scheduled' | 'attended' | 'missed' | 'cancelled';
  disabled?: boolean;
  loading?: boolean;
  onMark: (status: AttendanceMarkStatus | 'scheduled') => void;
}

const STATUS_OPTIONS: Array<{
  status: AttendanceMarkStatus;
  label: string;
  Icon: typeof CheckCircle2;
  activeClass: string;
}> = [
  {
    status: 'attended',
    label: 'Present',
    Icon: CheckCircle2,
    activeClass:
      'bg-green-100 text-green-700 dark:bg-green-900/60 dark:text-green-300 border-green-200 dark:border-green-800',
  },
  {
    status: 'missed',
    label: 'Absent',
    Icon: XCircle,
    activeClass:
      'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300 border-red-200 dark:border-red-800',
  },
  {
    status: 'cancelled',
    label: 'Cancelled',
    Icon: Ban,
    activeClass:
      'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600',
  },
];

export function AttendanceToggle({
  current = 'scheduled',
  disabled = false,
  loading = false,
  onMark,
}: AttendanceToggleProps) {
  const handleClick = (status: AttendanceMarkStatus) => {
    if (disabled || loading) return;
    onMark(current === status ? 'scheduled' : status);
  };

  return (
    <div
      className="inline-flex shrink-0 items-center rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden"
      role="group"
      aria-label="Mark attendance"
    >
      {loading ? (
        <div className="flex h-7 w-[5.25rem] sm:h-8 sm:w-[6.5rem] items-center justify-center bg-transparent">
          <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin text-gray-500" aria-hidden />
        </div>
      ) : (
        STATUS_OPTIONS.map(({ status, label, Icon, activeClass }) => {
          const isActive = current === status;
          return (
            <button
              key={status}
              type="button"
              title={label}
              aria-label={label}
              aria-pressed={isActive}
              disabled={disabled}
              onClick={() => handleClick(status)}
              className={`flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center border-r border-gray-200 dark:border-gray-600 last:border-r-0 transition-colors ${
                isActive
                  ? activeClass
                  : 'bg-transparent text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
            </button>
          );
        })
      )}
    </div>
  );
}
